import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { IndianRupee, FileText, Upload, X, Loader2, Send, Save } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import api from '../services/api';
import { QuotationType, QuotationStatus, DocumentTypeCode, DocumentReferenceType } from '../constants/stageConstants';

const AdditionalWorkModal = ({ open, onOpenChange, project, onQuotationCreated, existingQuotation = null }) => {
    const [extraWorkDescription, setExtraWorkDescription] = useState('');
    const [extraWorkAmount, setExtraWorkAmount] = useState('');
    const [gstPercentage, setGstPercentage] = useState(18);
    const [remarks, setRemarks] = useState('');
    const [uploadedDocuments, setUploadedDocuments] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    // Calculated
    const amount = parseFloat(extraWorkAmount) || 0;
    const gstAmount = amount * (gstPercentage / 100);
    const netAmount = amount + gstAmount;

    // Pre-fill when editing
    useEffect(() => {
        if (open && existingQuotation) {
            setExtraWorkDescription(existingQuotation.extraWorkDescription || '');
            setExtraWorkAmount(existingQuotation.extraWorkAmount || '');
            setGstPercentage(existingQuotation.gstPercentage ?? 18);
            setRemarks(existingQuotation.notes || existingQuotation.remarks || '');
        } else if (open) {
            setExtraWorkDescription('');
            setExtraWorkAmount('');
            setGstPercentage(18);
            setRemarks('');
            setUploadedDocuments([]);
        }
    }, [open, existingQuotation]);

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files);
        const newDocs = files.map(f => ({ file: f, name: f.name, size: f.size }));
        setUploadedDocuments(prev => [...prev, ...newDocs]);
        e.target.value = '';
    };

    const removeDocument = (index) => {
        setUploadedDocuments(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (status) => {
        if (!extraWorkDescription.trim()) {
            toast({ title: 'Required', description: 'Please enter a work description', variant: 'destructive' });
            return;
        }
        if (amount <= 0) {
            toast({ title: 'Required', description: 'Please enter a valid amount', variant: 'destructive' });
            return;
        }

        setSubmitting(true);
        try {
            const quotationData = {
                projectId: project.id || project.Id,
                quotationType: QuotationType.AdditionalWork,
                status,

                extraWorkAmount: amount,
                extraWorkDescription: extraWorkDescription.trim(),
                discountType: 'percentage',
                discountValue: 0,
                gstPercentage,
                baseWorkAmount: 0,
                subtotal: amount,
                discountAmount: 0,
                taxableAmount: amount,
                gstAmount,
                netAmount,
                remarks: remarks || null
            };

            let response;
            if (existingQuotation) {
                response = await api.updateQuotation(existingQuotation.id, quotationData);
            } else {
                response = await api.createQuotation(quotationData);
            }

            // Upload documents
            if (uploadedDocuments.length > 0) {
                for (const doc of uploadedDocuments) {
                    try {
                        const formData = new FormData();
                        formData.append('file', doc.file);
                        formData.append('documentTypeCode', DocumentTypeCode.QuotationPdf);
                        formData.append('referenceType', DocumentReferenceType.Quotation);
                        formData.append('referenceId', response.quotationId);
                        await api.post(`/api/projects/${project.id || project.Id}/documents/upload`, formData);
                    } catch (err) {
                        console.error('Failed to upload document:', doc.name, err);
                    }
                }
            }

            toast({
                title: status === QuotationStatus.Draft ? 'Draft Saved' : 'Quotation Sent',
                description: status === QuotationStatus.Draft ? 'Additional work quotation saved as draft' : 'Additional work quotation sent to OEM'
            });
            onOpenChange(false);
            onQuotationCreated?.();
        } catch (error) {
            toast({ title: 'Error', description: error.message || 'Failed to create quotation', variant: 'destructive' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg max-h-[85dvh] sm:max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{existingQuotation ? 'Edit Additional Work' : 'New Additional Work Quotation'}</DialogTitle>
                    <DialogDescription>Create a quotation for additional work on this project</DialogDescription>
                </DialogHeader>

                <div className="space-y-4 mt-4">
                    {/* Work Description */}
                    <div>
                        <Label>Work Description *</Label>
                        <Textarea
                            placeholder="Describe the additional work to be done..."
                            value={extraWorkDescription}
                            onChange={(e) => setExtraWorkDescription(e.target.value)}
                            rows={3}
                        />
                    </div>

                    {/* Amount */}
                    <div>
                        <Label>Amount *</Label>
                        <div className="relative">
                            <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                                type="number"
                                placeholder="Enter amount"
                                className="pl-9"
                                value={extraWorkAmount}
                                onChange={(e) => setExtraWorkAmount(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* GST */}
                    <div>
                        <Label>GST %</Label>
                        <Input
                            type="number"
                            value={gstPercentage}
                            onChange={(e) => setGstPercentage(parseFloat(e.target.value) || 0)}
                        />
                    </div>

                    {/* Summary */}
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Amount</span>
                            <span className="font-medium">₹{amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-600">GST ({gstPercentage}%)</span>
                            <span className="font-medium">₹{gstAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                        </div>
                        <div className="border-t pt-2 flex justify-between text-base font-bold">
                            <span>Net Amount</span>
                            <span className="text-teal-700">₹{netAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                        </div>
                    </div>

                    {/* Remarks */}
                    <div>
                        <Label>Remarks</Label>
                        <Textarea
                            placeholder="Any additional notes..."
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            rows={2}
                        />
                    </div>

                    {/* Documents */}
                    <div>
                        <Label>Documents</Label>
                        <div className="mt-1">
                            <label className="flex items-center gap-2 px-3 py-2 border-2 border-dashed rounded-lg cursor-pointer hover:border-teal-400 transition-colors">
                                <Upload className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-500">Upload documents (PDF, images, Word)</span>
                                <input type="file" className="hidden" multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={handleFileUpload} />
                            </label>
                            {uploadedDocuments.length > 0 && (
                                <div className="mt-2 space-y-1">
                                    {uploadedDocuments.map((doc, i) => (
                                        <div key={i} className="flex items-center justify-between bg-gray-50 rounded px-3 py-1.5 text-sm">
                                            <div className="flex items-center gap-2">
                                                <FileText className="w-3.5 h-3.5 text-gray-400" />
                                                <span className="truncate max-w-[200px]">{doc.name}</span>
                                            </div>
                                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => removeDocument(i)}>
                                                <X className="w-3.5 h-3.5" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 pt-2">
                        <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => handleSubmit(QuotationStatus.Draft)}
                            disabled={submitting}
                        >
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                            Save Draft
                        </Button>
                        <Button
                            className="flex-1 bg-teal-600 hover:bg-teal-700"
                            onClick={() => handleSubmit(QuotationStatus.Sent)}
                            disabled={submitting}
                        >
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                            Share with OEM
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AdditionalWorkModal;
